import { memo, useCallback, useMemo, useState } from 'react';

import { type ItemTypeFilter } from '~/components/Filters/types';
import { PrivatePageSearchActions } from '~/components/Search/PrivatePageSearchActions';
import { type BasePrivateSearchProps, PrivateSearch, type PrivateSearchProps } from '~/components/Search/PrivateSearch';
import { useSearchParams } from '~/hooks/search/useSearchParams';
import { useStickyResult } from '~/hooks/useStickyResult';
import { usePrivateSearchSuggestions } from '~/swr-hooks/search/usePrivateSearchSuggestions';

type PrivatePageSearchProps = BasePrivateSearchProps;

export const PrivatePageSearch = memo(
  ({
    compact,
    defaultOpen,
    trackLocation,
    onCloseClick,
    placeholder,
    hideFilterBar = false,
  }: PrivatePageSearchProps) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isFocused, setIsFocused] = useState(defaultOpen ?? false);
    const { search } = useSearchParams();

    const { data, refetch } = usePrivateSearchSuggestions({
      search: searchTerm,
      canFetch: isFocused,
    });

    // this hook prevents passing empty data to SearchBar while suggestions are loading
    // It is used to keep previous suggestions visible while new ones are not returned yet
    const suggestions = useStickyResult(data);

    const onFocusChange = useCallback(
      (isFocused: boolean) => {
        setIsFocused(isFocused);
        if (isFocused) {
          refetch();
        }
      },
      [refetch],
    );

    const availableFilterTypes: ItemTypeFilter[] = useMemo(() => {
      /**
       * Only show boards filter if search is not empty
       */
      if (search) {
        return ['assets', 'files', 'boards'];
      }

      return ['assets', 'files'];
    }, [search]);

    const renderActions = useCallback<Required<PrivateSearchProps>['renderActions']>(({ onClearSearchValue }) => {
      return <PrivatePageSearchActions onClearSearchValue={onClearSearchValue} />;
    }, []);

    return (
      <PrivateSearch
        compact={compact}
        defaultOpen={defaultOpen}
        trackLocation={trackLocation}
        onCloseClick={onCloseClick}
        onSearchChange={setSearchTerm}
        onFocusChange={onFocusChange}
        suggestions={suggestions}
        placeholder={placeholder}
        availableFilterTypes={availableFilterTypes}
        renderActions={renderActions}
        hideFilterBar={hideFilterBar}
      />
    );
  },
);

PrivatePageSearch.displayName = 'PrivatePageSearch';
